// ═══════════════════════════════════════════════════════════════════════════════
// useTokenPrice — Professional RPC-based token price fetching
// Fetches prices directly from Nad.fun Lens contract on Monad
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useEffect, useRef } from 'react';
import { useReadContract } from 'wagmi';
import { formatEther, parseEther } from 'viem';
import { NADFUN_CONTRACTS, LENS_ABI } from '../config/abi';
import { TokenConfig } from '../lib/WebAgentEngine';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface TokenPriceData {
    /** Price of 1 token in MON (always available for bonding curve tokens) */
    priceInMon: number;
    /** Price of 1 token in USD (available when MON/USD rate is known) */
    priceUsd: number;
    /** MON/USD exchange rate */
    monUsdRate: number;
    /** Historical price points (USD), most recent first */
    priceHistory: number[];
    /** Whether the hook is currently loading initial data */
    isLoading: boolean;
    /** Error message if price fetch failed */
    error: string | null;
    /** Last successful update timestamp */
    lastUpdated: number;
    /** Whether this token is the native/wrapped native asset */
    isNativeAsset: boolean;
    /** Bonding curve progress (0-100) */
    bondingProgress: number;
    /** Whether the token has graduated from the bonding curve */
    isGraduated: boolean;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const PRICE_HISTORY_MAX = 50;
const REFETCH_INTERVAL = 900_000; // 15 minutes
const MON_USD_CACHE_TTL = 60_000; // Cache MON/USD rate for 60 seconds

// Module-level MON/USD cache (shared across all hook instances)
let cachedMonUsdRate = 0;
let cachedMonUsdTimestamp = 0;

// ─── MON/USD Fetcher ─────────────────────────────────────────────────────────

async function fetchMonUsdRate(): Promise<number> {
    // Return cached value if fresh
    if (cachedMonUsdRate > 0 && Date.now() - cachedMonUsdTimestamp < MON_USD_CACHE_TTL) {
        return cachedMonUsdRate;
    }

    try {
        // Try DexScreener for WMON/USD pair
        const res = await fetch(
            `https://api.dexscreener.com/latest/dex/tokens/${NADFUN_CONTRACTS.WMON}`
        );
        const data = await res.json();

        if (data.pairs && data.pairs.length > 0) {
            // Find the pair with the most liquidity
            const bestPair = data.pairs.reduce((best: any, pair: any) => {
                const liq = parseFloat(pair.liquidity?.usd || '0');
                return liq > (parseFloat(best.liquidity?.usd || '0')) ? pair : best;
            }, data.pairs[0]);

            const rate = parseFloat(bestPair.priceUsd || '0');
            if (rate > 0) {
                cachedMonUsdRate = rate;
                cachedMonUsdTimestamp = Date.now();
                return rate;
            }
        }
    } catch (e) {
        console.warn('[useTokenPrice] DexScreener MON/USD fetch failed:', e);
    }

    // Fallback: try CoinGecko-compatible endpoint
    try {
        const res = await fetch(
            'https://api.coingecko.com/api/v3/simple/price?ids=monad&vs_currencies=usd'
        );
        const data = await res.json();
        if (data.monad?.usd) {
            cachedMonUsdRate = data.monad.usd;
            cachedMonUsdTimestamp = Date.now();
            return cachedMonUsdRate;
        }
    } catch {
        // Silently fallback
    }

    // Last resort: use cached value even if stale, or 0
    return cachedMonUsdRate || 0;
}

// ─── DexScreener Price History Seeder ─────────────────────────────────────────

/**
 * Fetch recent price points from DexScreener to seed the price history immediately.
 * This gives technical indicators (RSI, MACD) enough data to calculate on first render
 * instead of waiting 2-5 minutes for RPC polling to accumulate points.
 */
async function seedPriceHistoryFromDexScreener(tokenAddress: string): Promise<number[]> {
    try {
        const res = await fetch(
            `https://api.dexscreener.com/latest/dex/tokens/${tokenAddress}`,
            { signal: AbortSignal.timeout(8000) }
        );
        if (!res.ok) return [];
        const data = await res.json();

        if (!data.pairs || data.pairs.length === 0) return [];

        // Pick best Monad pair by liquidity
        const monadPairs = data.pairs.filter((p: any) => p.chainId === 'monad');
        const pair = monadPairs.length > 0
            ? monadPairs.sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0]
            : data.pairs.sort((a: any, b: any) => (b.liquidity?.usd || 0) - (a.liquidity?.usd || 0))[0];

        if (!pair) return [];

        const currentPrice = parseFloat(pair.priceUsd || '0');
        if (currentPrice <= 0) return [];

        // Use available price change data to generate synthetic history points.
        // DexScreener provides priceChange for various timeframes.
        const change5m = pair.priceChange?.m5 || 0;
        const change1h = pair.priceChange?.h1 || 0;
        const change6h = pair.priceChange?.h6 || 0;
        const change24h = pair.priceChange?.h24 || 0;

        // Build 30 historical price points spread over different timeframes
        // This gives enough data for RSI (14 periods) and MACD (26 periods)
        const history: number[] = [currentPrice];

        // Interpolate prices backwards using the change percentages
        const milestones = [
            { points: 5, changePct: change5m },     // 5 points over 5min change
            { points: 7, changePct: change1h },      // 7 points over 1h change
            { points: 8, changePct: change6h },      // 8 points over 6h change
            { points: 10, changePct: change24h },    // 10 points over 24h change
        ];

        let lastPrice = currentPrice;
        for (const milestone of milestones) {
            const startPrice = lastPrice / (1 + milestone.changePct / 100);
            const step = (lastPrice - startPrice) / milestone.points;
            for (let i = milestone.points - 1; i >= 0; i--) {
                // Add slight noise to avoid perfectly linear interpolation
                const noise = 1 + (Math.random() - 0.5) * 0.002; // +/- 0.1%
                const price = startPrice + step * i * noise;
                history.push(price);
            }
            lastPrice = startPrice;
        }

        // Return most recent first, capped at PRICE_HISTORY_MAX
        return history.slice(0, PRICE_HISTORY_MAX);
    } catch {
        return [];
    }
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useTokenPrice(token: TokenConfig | null): TokenPriceData {
    const [priceInMon, setPriceInMon] = useState(0);
    const [priceUsd, setPriceUsd] = useState(0);
    const [monUsdRate, setMonUsdRate] = useState(cachedMonUsdRate);
    const [priceHistory, setPriceHistory] = useState<number[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState(0);
    const [bondingProgress, setBondingProgress] = useState(0);
    const [isGraduated, setIsGraduated] = useState(false);

    const prevTokenRef = useRef<string | null>(null);

    // Detect if this is the native asset (MON)
    const isNativeAsset = !!(
        token &&
        (token.address.toLowerCase() === NADFUN_CONTRACTS.WMON.toLowerCase() ||
            token.symbol === 'MON' ||
            token.symbol === 'WMON')
    );

    // ── RPC: getAmountOut (1 token → MON) ────────────────────────────────────
    const { data: amountOutData, isLoading: rpcLoading, error: rpcError } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'getAmountOut',
        args: [
            (token?.address || '0x0000000000000000000000000000000000000000') as `0x${string}`,
            parseEther('1'),
            false // isBuy = false → selling 1 token, get MON back
        ],
        query: {
            enabled: !!token && !isNativeAsset,
            refetchInterval: REFETCH_INTERVAL,
        },
    });

    // ── RPC: getProgress ─────────────────────────────────────────────────────
    const { data: progressData } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'getProgress',
        args: [
            (token?.address || '0x0000000000000000000000000000000000000000') as `0x${string}`,
        ],
        query: {
            enabled: !!token && !isNativeAsset,
            refetchInterval: REFETCH_INTERVAL,
        },
    });

    // ── RPC: isGraduated ─────────────────────────────────────────────────────
    const { data: graduatedData } = useReadContract({
        address: NADFUN_CONTRACTS.LENS as `0x${string}`,
        abi: LENS_ABI,
        functionName: 'isGraduated',
        args: [
            (token?.address || '0x0000000000000000000000000000000000000000') as `0x${string}`,
        ],
        query: {
            enabled: !!token && !isNativeAsset,
            refetchInterval: REFETCH_INTERVAL,
        },
    });

    // ── Reset state when token changes ───────────────────────────────────────
    useEffect(() => {
        if (!token) return;
        const currentAddr = token.address.toLowerCase();
        if (prevTokenRef.current !== currentAddr) {
            prevTokenRef.current = currentAddr;
            setPriceInMon(0);
            setPriceUsd(0);
            setPriceHistory([]);
            setError(null);
            setIsLoading(true);
            setBondingProgress(0);
            setIsGraduated(false);

            // Seed price history from DexScreener for instant indicator calculation
            seedPriceHistoryFromDexScreener(currentAddr).then(history => {
                if (history.length > 0 && prevTokenRef.current === currentAddr) {
                    setPriceHistory(history);
                }
            });
        }
    }, [token]);

    // ── Fetch MON/USD rate ───────────────────────────────────────────────────
    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            const rate = await fetchMonUsdRate();
            if (!cancelled && rate > 0) {
                setMonUsdRate(rate);
            }
        };

        load();
        const interval = setInterval(load, MON_USD_CACHE_TTL);
        return () => { cancelled = true; clearInterval(interval); };
    }, []);

    // ── Process RPC price data ───────────────────────────────────────────────
    useEffect(() => {
        if (!token) return;

        // Native asset: price IS the MON/USD rate
        if (isNativeAsset) {
            setPriceInMon(1);
            const usd = monUsdRate || 0;
            setPriceUsd(usd);
            if (usd > 0) {
                setPriceHistory(prev => {
                    const newHistory = [usd, ...prev].slice(0, PRICE_HISTORY_MAX);
                    return newHistory;
                });
            }
            setIsLoading(false);
            setLastUpdated(Date.now());
            return;
        }

        // Bonding curve token: use getAmountOut result
        if (amountOutData) {
            try {
                const result = amountOutData as readonly [string, bigint];
                const monAmount = result[1];
                const tokenPriceInMon = parseFloat(formatEther(monAmount));

                if (tokenPriceInMon > 0) {
                    setPriceInMon(tokenPriceInMon);
                    const usdPrice = monUsdRate > 0 ? tokenPriceInMon * monUsdRate : 0;
                    setPriceUsd(usdPrice);
                    setError(null);
                    setLastUpdated(Date.now());

                    // Add to history (use MON price if no USD rate)
                    const historyValue = usdPrice > 0 ? usdPrice : tokenPriceInMon;
                    setPriceHistory(prev => {
                        const newHistory = [historyValue, ...prev].slice(0, PRICE_HISTORY_MAX);
                        return newHistory;
                    });
                }
            } catch (e) {
                console.error('[useTokenPrice] Failed to parse RPC data:', e);
                setError('Failed to parse price data');
            }
            setIsLoading(false);
        } else if (rpcError) {
            setError('RPC price fetch failed');
            setIsLoading(false);
        }
    }, [amountOutData, rpcError, monUsdRate, token, isNativeAsset]);

    // ── Process progress data ────────────────────────────────────────────────
    useEffect(() => {
        if (progressData !== undefined && progressData !== null) {
            // Progress is returned as uint256 (0-10000 representing 0-100%)
            const progress = Number(progressData) / 100;
            setBondingProgress(Math.min(progress, 100));
        }
    }, [progressData]);

    // ── Process graduated data ───────────────────────────────────────────────
    useEffect(() => {
        if (graduatedData !== undefined && graduatedData !== null) {
            setIsGraduated(graduatedData as boolean);
        }
    }, [graduatedData]);

    return {
        priceInMon,
        priceUsd,
        monUsdRate,
        priceHistory,
        isLoading: isLoading || rpcLoading,
        error,
        lastUpdated,
        isNativeAsset,
        bondingProgress,
        isGraduated,
    };
}

export default useTokenPrice;
